'use client'

import { createContext, useContext, type ReactNode } from 'react'
import { useUser, type UserData, type UseUserReturn } from '@/hooks/use-user'
import { isAdminRole, isManagerOrAboveRole, type UserRole } from '@/lib/permissions'

/**
 * Auth context value
 */
interface AuthContextValue extends UseUserReturn {
  /** Whether the user is authenticated */
  isAuthenticated: boolean
  /** Quick check if user has a specific role */
  hasRole: (role: UserRole) => boolean
  /** Quick check if user is admin */
  isAdmin: boolean
  /** Quick check if user is at least manager */
  isManagerOrAbove: boolean
}

const AuthContext = createContext<AuthContextValue | null>(null)

/**
 * Auth Provider Props
 */
interface AuthProviderProps {
  children: ReactNode
  /**
   * Server-resolved user passed from the root layout. Required because the
   * browser Supabase client cannot read httpOnly session cookies.
   */
  initialUser?: UserData | null
}

/**
 * Auth Provider Component
 *
 * Wraps your app to provide authentication state globally.
 * Uses the useUser hook internally and adds convenience methods.
 *
 * @example
 * // In your layout.tsx
 * export default function RootLayout({ children }) {
 *   return (
 *     <html>
 *       <body>
 *         <AuthProvider>
 *           {children}
 *         </AuthProvider>
 *       </body>
 *     </html>
 *   )
 * }
 *
 * // In any component
 * function MyComponent() {
 *   const { user, isAdmin, signOut } = useAuth()
 *   if (isAdmin) {
 *     return <AdminPanel />
 *   }
 * }
 */
export function AuthProvider({ children, initialUser = null }: AuthProviderProps) {
  const userHook = useUser({ initialUser })
  const activeUser = userHook.user?.is_active === false ? null : userHook.user

  const isAdmin = activeUser ? isAdminRole(activeUser.role) : false

  const value: AuthContextValue = {
    ...userHook,
    user: activeUser,
    isAuthenticated: !!activeUser,
    hasRole: (role: UserRole) => activeUser?.role === role,
    isAdmin,
    isManagerOrAbove: activeUser ? isManagerOrAboveRole(activeUser.role) : false,
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

/**
 * Hook to access auth context
 *
 * @throws Error if used outside of AuthProvider
 *
 * @example
 * const { user, isAdmin, signOut, isLoading } = useAuth()
 */
export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext)

  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }

  return context
}

/**
 * Type exports
 */
export type { AuthContextValue, UserData }
